import cn from "classnames";
import React, { useState } from "react";
import { Button } from "components/UI/buttons/Button";
import { Loader } from "components/shared/Loader";

import styles from "./CGForm.module.scss";

const API_URL = "https://essay-toolbox.sky-services.cc/api/conclusion_generator/create";
const MIN_TITLE_CHARACTERS = 5;
const MAX_TITLE_CHARACTERS = 150;
const MIN_TEXT_WORDS = 500;

const countWords = (str: string): number => str.trim().split(/\s+/).length;

const FormItem = ({
  label,
  children,
  error,
  isError,
}: {
  children: React.ReactNode;
  error: string;
  isError: boolean;
  label?: string;
}) => {
  return (
    <div className={styles.formItem}>
      {label && <span className={styles.labelText}>{label}</span>}
      {children}
      <div className={styles.formItemErrorWrap}>
        {isError && <div className={styles.formItemError}>{error}</div>}
      </div>
    </div>
  );
};

type FormFieldType = "title" | "text";

type FormStateType = Record<
  FormFieldType,
  {
    value: string | boolean;
    isError: boolean;
    error: string;
  }
>;

export const CGForm = ({
  setText,
  setSummaryData,
  setIsSummary,
}: {
  setText: (text: string) => void;
  setSummaryData: (data: Record<string, string | number>) => void;
  setIsSummary: (val: boolean) => void;
}) => {
  const initialState = {
    title: {
      value: "",
      isError: false,
      error: `Essay Title is required. Please provide a title that is between ${MIN_TITLE_CHARACTERS} and ${MAX_TITLE_CHARACTERS} characters.`,
    },
    text: {
      value: "",
      isError: false,
      error: `Please provide an Essay which is at least ${MIN_TEXT_WORDS} words.`,
    },
  };

  const [formState, setFormState] = useState<FormStateType>(initialState);

  const formText = formState.text.value;

  const [load, setLoad] = useState(false);

  const requestSummary = async (text: string) => {
    const fetchBody = {
      textToSummarize: text,
    };
    await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(fetchBody),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.textSummarize) {
          setSummaryData({ ...data });
          setIsSummary(true);
        }
      })
      .catch((error) => console.log(error, "req error"));
  };

  const validateField = (name: FormFieldType, value: string) => {
    const fieldsConditions = {
      title: value.length < MIN_TITLE_CHARACTERS,
      text: countWords(value) < MIN_TEXT_WORDS,
    };

    setFormState({
      ...formState,
      [name]: {
        ...formState[name],
        isError: fieldsConditions[name],
        value,
      },
    });
  };

  const validateFields = () => {
    const fieldsConditions = {
      title: String(formState.title.value).length < MIN_TITLE_CHARACTERS,
      text: countWords(String(formState.text.value)) < MIN_TEXT_WORDS,
    };

    setFormState({
      ...formState,
      title: {
        ...formState.title,
        isError: fieldsConditions.title,
      },
      text: {
        ...formState.text,
        isError: fieldsConditions.text,
      },
    });
    return Object.values(fieldsConditions).filter((item) => item).length > 0;
  };

  const handleInputChange = (name: FormFieldType, value: string) => {
    setFormState({
      ...formState,
      [name]: {
        ...formState[name],
        value,
      },
    });

    if (formState[name].isError) validateField(name, value);
  };

  const onFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isErrors = validateFields();

    if (isErrors) return;
    setText(String(formText));
    setLoad(true);
    await requestSummary(String(formText)).catch(() => setLoad(false));
  };

  return (
    <form onSubmit={onFormSubmit} className={styles.form}>
      <FormItem label="Title" isError={formState.title.isError} error={formState.title.error}>
        <input
          onChange={({ target }) => handleInputChange(target.name as FormFieldType, target.value)}
          onBlur={({ target }) => validateField(target.name as FormFieldType, target.value)}
          name="title"
          type="text"
          placeholder={`Enter the title of your paper (min ${MIN_TITLE_CHARACTERS} characters)`}
          className={cn(styles.input, styles.title)}
          readOnly={load}
        />
      </FormItem>
      <FormItem label="Text" isError={formState.text.isError} error={formState.text.error}>
        <textarea
          onChange={({ target }) => handleInputChange(target.name as FormFieldType, target.value)}
          onBlur={({ target }) => validateField(target.name as FormFieldType, target.value)}
          value={String(formText)}
          name="text"
          id="text"
          rows={10}
          placeholder={`Enter the text of your paper (min ${MIN_TEXT_WORDS} words)`}
          className={cn(styles.input, styles.text)}
          readOnly={load}
        />
      </FormItem>

      <div className={styles.buttonWrap}>
        <Button tag="button" type="secondary" htmlType="submit" className={styles.buttonMain} disabled={load}>
          {load ? <Loader /> : "Generate"}
        </Button>
      </div>
    </form>
  );
};
